import React from "react";
import {
  Button,
  Dialog,
  DialogSurface,
  DialogTitle,
  Spinner,
} from "@fluentui/react-components";
import MaterialReactTable, {
  MRT_ColumnDef,
  MRT_SortingState,
} from "material-react-table";
import { FilterDismiss20Regular } from "@fluentui/react-icons";
import { ReportParameter } from "../../../types/reportTypes";
import ASCButton from "../../../components/Button";
import { popupRowId } from "../rowIdentity";
import { SEARCH_MIN_CHARS } from "../hooks/useReportForm";

// The Client List endpoint's full sortBy whitelist (customer.service.ts's
// resolveClientListSortColumn) -- a column only gets a clickable sort header
// if its value is one of these, so non-client parameters (whose column
// values never match) are unaffected without needing a separate "is this the
// Client parameter" check.
const SORTABLE_COLUMN_VALUES = new Set([
  "first_names",
  "last_name",
  "partner_first_names",
  "partner_last_name",
  "consultant_name",
]);

interface PopupState {
  data: any[];
  selected: any | null;
  total: number;
  isOpen: boolean;
  pagination: { pageIndex: number; pageSize: number };
  searchQuery: string;
  rowSelection: { [key: string]: boolean };
  isFetchingMore?: boolean;
  sorting?: MRT_SortingState;
}

interface PopupTableProps {
  param: ReportParameter;
  popupState: PopupState;
  onClose: () => void;
  // Cancel, Escape and clicking outside the dialog. Defaults to onClose;
  // ReportForm passes its own so a cancelled search is cleared, while Select
  // (which still goes through onClose) keeps it.
  onCancel?: () => void;
  onSelect: (selected: any) => void;
  onRowSelectionChange: (updater: any) => void;
  onPaginationChange: (updater: any) => void;
  // Optional: callers whose parameter type never sets sortBy/sortDirection
  // in QueryParams (e.g. CashCalcPage's client picker) have no sort state to
  // update and can omit this -- sortableByBackend below already keeps the
  // table from ever calling it in that case.
  onSortingChange?: (updater: any) => void;
  onGlobalFilterChange: (value: string) => void;
  useScrollPagination?: boolean;
  onLoadMore?: () => void;
  // True while this parameter's rows are being fetched — the initial load and,
  // more visibly, every server-side search. Without it the table sat showing
  // the previous result set with no sign anything was happening.
  loading?: boolean;
}

const PopupTable: React.FC<PopupTableProps> = ({
  param,
  popupState,
  onClose,
  onCancel = onClose,
  onSelect,
  onRowSelectionChange,
  onPaginationChange,
  onSortingChange,
  onGlobalFilterChange,
  useScrollPagination = false,
  onLoadMore,
  loading = false,
}) => {
  // Sorting is only meaningful server-side (manualSorting below) and only
  // for the columns the Client List endpoint actually knows how to sort by;
  // everything else keeps its header non-interactive rather than firing a
  // sort request the backend would ignore.
  const sortableByBackend = param.apiEndpoint?.QueryParams?.includes("sortBy");

  const getColumnsForPopup = (param: any): MRT_ColumnDef<any>[] => {
    if (param.apiEndpoint && Array.isArray(param.apiEndpoint.attributes)) {
      return param.apiEndpoint.attributes.map((attr: any) => ({
        accessorKey: attr.value,
        header: attr.label,
        enableSorting: sortableByBackend && SORTABLE_COLUMN_VALUES.has(attr.value),
        Cell: ({ cell }: any) => {
          const val = cell.getValue();
          if (val == null) return "-";
          if (attr.type === "datetime") {
            const d = new Date(val);
            return isNaN(d.getTime())
              ? val
              : d.toLocaleString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                  hour: "2-digit",
                  minute: "2-digit",
                });
          }
          if (attr.type === "date") {
            const d = new Date(val);
            return isNaN(d.getTime())
              ? val
              : d.toLocaleDateString("en-GB", {
                  day: "2-digit",
                  month: "short",
                  year: "numeric",
                });
          }
          return val;
        },
      }));
    }
    return [];
  };

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    if (!useScrollPagination || !onLoadMore) return;
    const { scrollTop, scrollHeight, clientHeight } = e.currentTarget;
    const nearBottom = scrollHeight - scrollTop - clientHeight < 60;
    const hasMore = (popupState.data?.length || 0) < (popupState.total || 0);
    if (nearBottom && !popupState.isFetchingMore && hasMore) {
      onLoadMore();
    }
  };

  return (
    <Dialog
      open={popupState.isOpen}
      onOpenChange={(_, { open }) => {
        if (!open) {
          onCancel();
        }
      }}
    >
      <DialogSurface>
        <DialogTitle>{param.placeholder}</DialogTitle>
        <MaterialReactTable
          columns={getColumnsForPopup(param)}
          data={popupState.data || []}
          // Key rows by their own identifier, not MRT's default array index.
          // With manual pagination an index-keyed selection means "row N of
          // whatever page is loaded", so a row picked on page 1 re-highlights
          // an unrelated row on page 2. See rowIdentity.ts.
          getRowId={(row, index) => popupRowId(param, row, index)}
          enableColumnFilters={false}
          enableColumnActions={false}
          enableDensityToggle={false}
          enableFullScreenToggle={false}
          enableHiding={false}
          // MRT's own alert banner (positionToolbarAlertBanner="top"/"bottom")
          // counts only rows in the currently-loaded page's row model, so under
          // manualPagination it drops selections made on earlier pages. It's
          // hidden here and replaced with a count derived directly from
          // popupState.rowSelection, which already accumulates across pages.
          positionToolbarAlertBanner="none"
          renderTopToolbarCustomActions={() => {
            const selectedCount = Object.values(
              popupState.rowSelection || {},
            ).filter(Boolean).length;
            if (selectedCount === 0) return null;
            return (
              <div style={{ display: "flex", alignItems: "center", gap: 4, paddingLeft: 8 }}>
                <span
                  style={{
                    fontSize: "0.875rem",
                    color: "rgba(0, 0, 0, 0.6)",
                  }}
                >
                  {selectedCount} of {popupState.total || 0} row(s) selected
                </span>
                <Button
                  appearance="subtle"
                  shape="circular"
                  style={{ minWidth: "auto" }}
                  icon={<FilterDismiss20Regular />}
                  aria-label="Clear selected rows"
                  title="Clear selected rows"
                  onClick={() => onRowSelectionChange({})}
                />
              </div>
            );
          }}
          enableRowSelection
          enablePagination={!useScrollPagination}
          manualPagination={!useScrollPagination}
          enableGlobalFilter={!useScrollPagination}
          // Searching is done SERVER-side (the query goes out as a `search`
          // param and comes back as a new page of rows), so MRT must not also
          // filter client-side. Without this it filtered the currently-loaded
          // page by the typed text the moment you started typing, found no
          // match in those ten rows, and rendered "No results found" before the
          // server had even replied. It would also keep hiding legitimate
          // server matches afterwards, whenever the server's matching rules
          // differ from MRT's fuzzy search over the displayed columns.
          manualFiltering
          // Coerced to a real boolean on purpose. MRT renders a RADIO only on a
          // strict `enableMultiRowSelection === false`; passing `undefined`
          // (a parameter whose metadata omits multiSelect) falls through to
          // MRT's own default of `true`, giving checkboxes and multi-select
          // behaviour on a single-select field.
          enableMultiRowSelection={param.multiSelect === true}
          // Sorting, like search, is resolved server-side -- the backend
          // returns the already-sorted page, so MRT must not also re-sort
          // the ten-or-so rows it's holding locally.
          manualSorting={sortableByBackend}
          enableMultiSort={false}
          rowCount={popupState.total || 0}
          state={{
            pagination: popupState.pagination || {
              pageIndex: 0,
              pageSize: useScrollPagination ? 20 : 10,
            },
            globalFilter: popupState.searchQuery || "",
            rowSelection: popupState.rowSelection || {},
            sorting: popupState.sorting || [],
            // Skeleton rows while fetching, plus a progress bar over the
            // toolbar so the search box itself shows activity.
            isLoading: loading,
            showProgressBars: loading,
          }}
          onRowSelectionChange={onRowSelectionChange}
          onGlobalFilterChange={(value) => onGlobalFilterChange(value || "")}
          onPaginationChange={onPaginationChange}
          onSortingChange={onSortingChange}
          muiTableContainerProps={{
            style: { height: "calc(100vh - 400px)" },
            onScroll: useScrollPagination ? handleScroll : undefined,
          }}
          initialState={{
            density: "compact",
            // Search box open from the start rather than hidden behind the
            // toggle. The toggle is disabled by MRT whenever a filter is
            // active, so relying on it to reveal the box is what made a
            // leftover query unrecoverable — with the box always visible the
            // text can be edited or cleared directly.
            showGlobalFilter: true,
          }}
          muiTablePaginationProps={{
            rowsPerPageOptions: [10, 20],
            showFirstButton: true,
            showLastButton: true,
          }}
          muiSearchTextFieldProps={{
            placeholder: `Search ${param.label}...`,
            sx: { minWidth: "300px" },
            // Says why nothing happens for the first couple of keystrokes —
            // the query is only sent once it reaches the minimum length.
            helperText: `Type at least ${SEARCH_MIN_CHARS} characters to search`,
          }}
        />
        {useScrollPagination && popupState.isFetchingMore && (
          <div
            style={{
              display: "flex",
              justifyContent: "center",
              padding: "8px",
            }}
          >
            <Spinner size="tiny" label="Loading more..." />
          </div>
        )}
        <div className="dialog_action_button">
          <ASCButton onClick={onCancel} label="Cancel" />
          <ASCButton
            className="asc-button-primary"
            appearance="primary"
            onClick={() => {
              const selected = popupState.selected;
              if (
                selected &&
                (Array.isArray(selected) ? selected.length > 0 : true)
              ) {
                onSelect(selected);
                onClose();
              }
            }}
            disabled={
              !popupState.selected ||
              (Array.isArray(popupState.selected) &&
                popupState.selected.length === 0)
            }
            label="Select"
          />
        </div>
      </DialogSurface>
    </Dialog>
  );
};

export default PopupTable;
